/**
 * 라이브러리에서 텍스트형 파일을 다시 만들 때 쓰는 기존 엔드포인트 래퍼.
 * 새 라우트는 만들지 않는다. 이미지 생성 API는 호출하지 않는다.
 */

import { getAuth } from 'firebase/auth';
import { getApiBaseUrl } from '../utils/apiConfig';
import { fetchWithRetry, calcApiTimeout } from '../utils/fetchWithRetry';
import { exportWorksheet } from './worksheetExportApi';
import { buildWorksheetCanvas, downloadWorksheetCanvasPdf, hasCanvas } from '../utils/canvasSchema';
import { applyPaperFormatToOutline, paperFormatFromOutline } from '../utils/worksheetPaperFormat';
import { FileDownloadError } from '../hooks/useFileDownload';

export const WORKSHEET_PDF_REMAKE_FAIL = 'PDF를 만들지 못했어요. 잠시 뒤 다시 시도해 주세요';
export const REMAKE_FILE_FAIL = '파일을 만들지 못했어요. 잠시 후 다시 시도해 주세요';

const detailText = (detail) => {
  if (typeof detail === 'string') return detail.trim();
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message.trim();
  }
  return '';
};

export const remakeUserMessage = (status, detail) => {
  const text = detailText(detail);
  if (!text) return REMAKE_FILE_FAIL;
  if (Number(status) >= 500) return REMAKE_FILE_FAIL;
  if (/[A-Za-z]/.test(text) && !/[가-힣]/.test(text)) return REMAKE_FILE_FAIL;
  return text;
};

const authHeaders = async () => {
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  try {
    const user = getAuth().currentUser;
    if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
  } catch (_error) {
    // 익명 — 기존 generate-file 익명 경로
  }
  return headers;
};

const readJson = async (response) => {
  try { return await response.json(); } catch (_error) { return null; }
};

export async function remakeTextPptFile({
  outline,
  filename,
  isProUser = false,
  conversationId = null,
  preferCanvas = false,
  signal,
} = {}) {
  if (!outline || typeof outline !== 'object') {
    throw new FileDownloadError('슬라이드 내용이 없어 텍스트형 PPT를 만들 수 없어요', { code: 'missing_outline' });
  }
  const path = preferCanvas ? '/api/ppt/canvas-to-pptx' : '/api/ppt/generate-file';
  const body = preferCanvas
    ? { outline, filename, is_pro_user: Boolean(isProUser) }
    : {
      outline,
      filename,
      generation_mode: 'text',
      is_pro_user: Boolean(isProUser),
      conversation_id: conversationId || undefined,
    };
  const response = await fetchWithRetry(
    `${getApiBaseUrl()}${path}`,
    { method: 'POST', headers: await authHeaders(), body: JSON.stringify(body), signal },
    { maxRetries: 0, timeoutMs: calcApiTimeout('default') },
  );
  const data = await readJson(response);
  if (!response.ok) {
    throw new FileDownloadError(
      remakeUserMessage(response.status, data?.detail?.message || data?.detail || data?.message),
      { code: `http_${response.status}` },
    );
  }
  const url = data?.download_url || data?.downloadUrl;
  if (!url) {
    throw new FileDownloadError('파일을 만들지 못했어요. 잠시 후 다시 시도해 주세요', { code: 'empty_url' });
  }
  return { url, filename: data.filename || filename || 'PPT.pptx' };
}

export async function remakeTextWorksheetExport({
  outline,
  format,
  filename,
  conversationId = null,
  signal,
} = {}) {
  const { record } = await exportWorksheet({
    outline,
    format,
    filename: String(filename || '학습지').replace(/\.[A-Za-z0-9]+$/, ''),
    conversationId,
    signal,
  });
  return { url: record.downloadUrl, filename: record.filename };
}

const throwIfAborted = (result) => {
  if (!result?.aborted) return;
  const error = new Error('Aborted');
  error.name = 'AbortError';
  throw error;
};

const hasRenderedWorksheetPages = (outline) => {
  const slides = outline?.slides;
  if (!Array.isArray(slides) || slides.length === 0) return false;
  return slides.every((slide) => hasCanvas(slide));
};

const outlineForWorksheetPdf = async (outline, signal) => {
  if (hasRenderedWorksheetPages(outline)) return outline;
  const format = paperFormatFromOutline(outline);
  const outlineForBuild = applyPaperFormatToOutline(outline, format);
  const built = await buildWorksheetCanvas({
    outline: outlineForBuild,
    worksheetStyle: outlineForBuild.worksheet_style,
    includeAnswerGuide: true,
    signal,
  });
  throwIfAborted(built);
  if (!built?.success || !hasRenderedWorksheetPages(built.outline)) {
    throw new FileDownloadError(WORKSHEET_PDF_REMAKE_FAIL, { code: 'ws_pdf_canvas' });
  }
  return built.outline;
};

export async function remakeTextWorksheetPdf({ outline, filename, signal } = {}) {
  if (!outline || typeof outline !== 'object') {
    throw new FileDownloadError(WORKSHEET_PDF_REMAKE_FAIL, { code: 'missing_outline' });
  }
  const canvasOutline = await outlineForWorksheetPdf(outline, signal);
  const result = await downloadWorksheetCanvasPdf({
    outline: canvasOutline,
    filename: String(filename || '학습지').replace(/\.[A-Za-z0-9]+$/, ''),
    signal,
  });
  throwIfAborted(result);
  if (!result?.success || !result?.download_url) {
    throw new FileDownloadError(WORKSHEET_PDF_REMAKE_FAIL, { code: 'ws_pdf' });
  }
  return { url: result.download_url, filename: result.filename || filename || '학습지.pdf' };
}
